import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  Trash2,
  Calendar,
  Crown,
  ShieldCheck,
  User as UserIcon,
} from "lucide-react";
import type { User } from "@/api/generated/client/models/User";

interface TeamMembersTableProps {
  members: User[];
  currentUserId?: number;
  isAdmin: boolean;
  canManageUser: (user: User) => boolean;
  onRoleChange: (userId: number, role: string) => void;
  onDeleteUser: (user: User) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

const getRoleBadge = (role: string) => {
  switch (role) {
    case "owner":
      return (
        <Badge variant="default" className="flex items-center gap-1">
          <Crown className="h-3 w-3" />
          Owner
        </Badge>
      );
    case "admin":
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" />
          Admin
        </Badge>
      );
    case "member":
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <UserIcon className="h-3 w-3" />
          Member
        </Badge>
      );
    default:
      return <Badge variant="outline">{role}</Badge>;
  }
};

export function TeamMembersTable({
  members,
  currentUserId,
  isAdmin,
  canManageUser,
  onRoleChange,
  onDeleteUser,
  isUpdating,
  isDeleting,
}: TeamMembersTableProps) {
  const isCurrentUser = (userId: number) => userId === currentUserId;

  return (
    <div className="rounded-md border overflow-x-auto md:overflow-visible">
      <Table className="min-w-[640px] md:min-w-0">
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="whitespace-nowrap md:whitespace-normal">Joined</TableHead>
            {isAdmin && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserIcon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div>{user.name || "No Name"}</div>
                    {isCurrentUser(user.id) && (
                      <span className="text-xs text-muted-foreground">(You)</span>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  {user.email}
                </div>
              </TableCell>
              <TableCell>
                {canManageUser(user) && user.role !== "owner" ? (
                  <Select
                    value={user.role}
                    onValueChange={(value) => onRoleChange(user.id, value)}
                    disabled={isUpdating}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  getRoleBadge(user.role)
                )}
              </TableCell>
              <TableCell className="w-[140px] whitespace-nowrap md:w-auto md:whitespace-normal">
                <div className="flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground md:whitespace-normal">
                  <Calendar className="h-4 w-4" />
                  {formatDate(user.createdAt)}
                </div>
              </TableCell>
              {isAdmin && (
                <TableCell className="text-right">
                  {canManageUser(user) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteUser(user)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
